import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Eraser,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Strikethrough,
  Underline,
} from 'lucide-react';

/**
 * WYSIWYG email-body editor. The editable email lives in a same-origin <iframe> with no
 * app CSS, so it renders the way a mail client does: inline styles AND the table-era
 * layout attributes email HTML relies on (align, bgcolor, width, cellpadding) behave
 * as they will in the inbox. (Rendering it inside the app page let Tailwind's reset
 * override them, e.g. right-aligned chat bubbles showed on the left.) The editor emits
 * inline styles (styleWithCSS) so formatting survives the server sanitizer, which
 * remains the security boundary (render.ts).
 */

const FRAME_DOC = `<!doctype html><html><head><meta charset="utf-8"><style>
html,body{margin:0;background:#ffffff}
body{padding:8px 12px;min-height:150px;font-family:Arial,Helvetica,sans-serif;font-size:14px;
line-height:1.4;color:#222222;outline:none;overflow-wrap:break-word}
</style></head><body contenteditable="true" role="textbox" aria-multiline="true" spellcheck="true"></body></html>`;

type Cmd = { icon: ReactElement; title: string; run: () => void };

/**
 * Parse HTML inertly (DOMParser never runs scripts) and drop anything active before it
 * enters the editor: script/style/embed tags, on* handlers, javascript:/data: URLs and
 * classes (classes don't survive email anyway). The server sanitizer remains the
 * security boundary; this keeps the editor honest and safe for pasted content.
 */
function cleanFragment(html: string, target: Document): DocumentFragment {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc
    .querySelectorAll('script,style,iframe,object,embed,link,meta,title,base,form,input,button')
    .forEach((n) => n.remove());
  doc.body.querySelectorAll('*').forEach((el) => {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase();
      const unsafeUrl =
        (name === 'href' || name === 'src') && /^\s*(javascript|vbscript|data):/i.test(attr.value);
      if (name.startsWith('on') || name === 'class' || unsafeUrl) el.removeAttribute(attr.name);
    }
  });
  const frag = target.createDocumentFragment();
  while (doc.body.firstChild) frag.appendChild(target.adoptNode(doc.body.firstChild));
  return frag;
}

export function RichTextEditor({
  value,
  onChange,
  ariaLabel = 'Email body',
}: {
  value: string;
  onChange: (html: string) => void;
  ariaLabel?: string;
}): ReactElement {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const docRef = useRef<Document | null>(null);
  const lastEmitted = useRef<string | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const emit = useCallback(() => {
    const doc = docRef.current;
    if (!doc) return;
    const html = doc.body.innerHTML;
    lastEmitted.current = html;
    onChangeRef.current(html);
  }, []);

  // Grow the frame with its content (images load late, so the body is observed).
  const resize = useCallback(() => {
    const frame = frameRef.current;
    const doc = docRef.current;
    if (frame && doc) frame.style.height = `${Math.max(160, doc.documentElement.scrollHeight)}px`;
  }, []);

  // Replace the editor content (initial load, or a new value from outside). Content is
  // cleaned first so a stored draft can never run handlers inside the app's origin.
  const setContent = useCallback(
    (html: string) => {
      const doc = docRef.current;
      if (!doc) return;
      doc.body.replaceChildren(cleanFragment(html, doc));
      lastEmitted.current = doc.body.innerHTML;
      resize();
    },
    [resize],
  );

  // Insert HTML at the caret via the DOM Range API. Unlike execCommand('insertHTML'),
  // this never runs the browser's "redundant style" removal, which (depending on the
  // browser version and page CSS) silently drops inline styles such as an image's
  // max-width:100%, so the email would differ from what the editor showed.
  const insertRaw = useCallback(
    (html: string) => {
      const doc = docRef.current;
      if (!doc) return;
      doc.body.focus();
      const sel = doc.getSelection();
      let range: Range;
      if (sel && sel.rangeCount > 0 && doc.body.contains(sel.getRangeAt(0).commonAncestorContainer)) {
        range = sel.getRangeAt(0);
        range.deleteContents();
      } else {
        range = doc.createRange();
        range.selectNodeContents(doc.body);
        range.collapse(false);
      }
      const frag = cleanFragment(html, doc);
      const last = frag.lastChild;
      range.insertNode(frag);
      if (last && sel) {
        range.setStartAfter(last);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      emit();
      resize();
    },
    [emit, resize],
  );

  const onFrameLoad = useCallback(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc) return;
    docRef.current = doc;
    doc.body.setAttribute('aria-label', ariaLabel);
    try {
      // Emit inline CSS (<span style="font-weight:bold">) rather than <b>/<font>.
      doc.execCommand('styleWithCSS', false, 'true');
    } catch {
      /* unsupported: tag-based formatting, still sanitized server-side */
    }
    setContent(valueRef.current);
    doc.addEventListener('input', () => {
      emit();
      resize();
    });
    doc.body.addEventListener('blur', emit);
    doc.addEventListener('paste', (e) => {
      const html = e.clipboardData?.getData('text/html');
      if (!html) return; // plain text: let the browser paste it normally
      e.preventDefault();
      insertRaw(html);
    });
    // Use the frame realm's ResizeObserver when present (observing its own nodes).
    const Observer =
      (frame.contentWindow as (Window & typeof globalThis) | null)?.ResizeObserver ?? ResizeObserver;
    new Observer(resize).observe(doc.body);
  }, [ariaLabel, emit, insertRaw, resize, setContent]);

  // Sync from outside only when the value is not the one just emitted (otherwise typing
  // would reset the caret on every keystroke).
  useEffect(() => {
    const doc = docRef.current;
    if (doc && value !== lastEmitted.current && value !== doc.body.innerHTML) setContent(value);
  }, [value, setContent]);

  const apply = (command: string, arg?: string) => {
    const doc = docRef.current;
    if (!doc) return;
    doc.body.focus();
    doc.execCommand(command, false, arg);
    emit();
    resize();
  };

  const addLink = () => {
    const url = window.prompt('Link URL (https://…)');
    if (!url) return;
    apply('createLink', url);
  };

  const addImage = () => {
    const url = window.prompt('Image URL (https://…). Use a hosted image so it renders in email.');
    if (!url) return;
    const safe = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    // Responsive image so large graphics fit the email column on phones.
    insertRaw(`<img src="${safe}" alt="" style="max-width:100%;height:auto" />`);
  };

  const groups: Cmd[][] = [
    [
      { icon: <Bold className="size-4" />, title: 'Bold', run: () => apply('bold') },
      { icon: <Italic className="size-4" />, title: 'Italic', run: () => apply('italic') },
      { icon: <Underline className="size-4" />, title: 'Underline', run: () => apply('underline') },
      { icon: <Strikethrough className="size-4" />, title: 'Strikethrough', run: () => apply('strikeThrough') },
    ],
    [
      { icon: <Heading1 className="size-4" />, title: 'Heading 1', run: () => apply('formatBlock', '<h1>') },
      { icon: <Heading2 className="size-4" />, title: 'Heading 2', run: () => apply('formatBlock', '<h2>') },
      { icon: <Heading3 className="size-4" />, title: 'Heading 3', run: () => apply('formatBlock', '<h3>') },
    ],
    [
      { icon: <List className="size-4" />, title: 'Bullet list', run: () => apply('insertUnorderedList') },
      { icon: <ListOrdered className="size-4" />, title: 'Numbered list', run: () => apply('insertOrderedList') },
    ],
    [
      { icon: <AlignLeft className="size-4" />, title: 'Align left', run: () => apply('justifyLeft') },
      { icon: <AlignCenter className="size-4" />, title: 'Align center', run: () => apply('justifyCenter') },
      { icon: <AlignRight className="size-4" />, title: 'Align right', run: () => apply('justifyRight') },
    ],
    [
      { icon: <LinkIcon className="size-4" />, title: 'Insert link', run: addLink },
      { icon: <ImageIcon className="size-4" />, title: 'Insert image', run: addImage },
    ],
    [{ icon: <Eraser className="size-4" />, title: 'Clear formatting', run: () => apply('removeFormat') }],
  ];

  const btn =
    'inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

  return (
    <div className="rounded-md border border-input bg-transparent shadow-sm">
      <div className="flex flex-wrap items-center gap-1 border-b p-1.5">
        {groups.map((group, gi) => (
          <div key={gi} className="flex items-center gap-0.5">
            {gi > 0 && <span className="mx-1 h-5 w-px bg-border" aria-hidden />}
            {group.map((c) => (
              <button
                key={c.title}
                type="button"
                className={btn}
                title={c.title}
                aria-label={c.title}
                // Keep the editor selection while clicking the toolbar.
                onMouseDown={(e) => e.preventDefault()}
                onClick={c.run}
              >
                {c.icon}
              </button>
            ))}
          </div>
        ))}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        <label className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Text color">
          <span className="sr-only">Text color</span>
          <input
            type="color"
            className="size-6 cursor-pointer rounded border bg-transparent p-0"
            onMouseDown={(e) => e.preventDefault()}
            onChange={(e) => apply('foreColor', e.target.value)}
            aria-label="Text color"
          />
        </label>
        <select
          className="h-7 rounded-md border border-input bg-transparent px-1.5 text-xs"
          title="Text size"
          aria-label="Text size"
          defaultValue=""
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            if (e.target.value) apply('fontSize', e.target.value);
            e.currentTarget.value = '';
          }}
        >
          <option value="" disabled>
            Size
          </option>
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="7">Huge</option>
        </select>
      </div>
      <iframe
        ref={frameRef}
        title={`${ariaLabel} editor`}
        srcDoc={FRAME_DOC}
        onLoad={onFrameLoad}
        className="block w-full rounded-b-md border-0 bg-white"
        style={{ height: 160 }}
      />
    </div>
  );
}
